import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { MainNavigation } from '@/components/MainNavigation';
import { QuickAnswerSection } from '@/components/QuickAnswerSection';
import { GateFAQSection } from '@/components/GateFAQSection';
import { Footer } from '@/components/Footer';
import { pricingPillarAIO } from '@/data/pricingPillarAIO';
import { loadAllArticles } from '@/services/articleLoader';
import { CTA1 } from '@/components/CTABanners';

const PRICING_PILLAR_SLUG = 'pricing-guide-2026';

export const PricingPillarPage = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const articles = await loadAllArticles();
        const found = articles.find(a => a.slug === PRICING_PILLAR_SLUG);
        if (mounted) {
          setArticle(found || null);
          setLoading(false);
        }
      } catch (e) {
        console.warn('Failed to load pricing guide pillar from /articles:', e);
        if (mounted) {
          setArticle(null);
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const quickAnswer = article?.quickAnswer || pricingPillarAIO.quickAnswer;
  const faqItems = article?.faqItems && article.faqItems.length ? article.faqItems : pricingPillarAIO.faqItems;

  if (loading && !article) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading guide...
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article?.title || 'IPTV Pricing Guide 2026 | XtreamGate'}</title>
        <meta
          name="description"
          content={
            article?.excerpt ||
            'Compare XtreamCube IPTV pricing in 2026 to typical cable bills – see plan options, costs, and realistic savings.'
          }
        />
        <link rel="canonical" href="https://xtreamgate.com/guides/pricing" />
      </Helmet>

      <div className="min-h-screen relative">
        <GridBackground />
        <MainNavigation />

        <main className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <Button asChild variant="outline" className="mb-8 glass border-primary/30">
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>

            {/* Page Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <DollarSign className="w-10 h-10 text-primary" />
                <h1 className="text-4xl sm:text-5xl font-bold text-white">
                  {article?.title || 'Pricing Plans'}
                </h1>
              </div>
              <p className="text-xl text-white/70 max-w-3xl mx-auto">
                {article?.excerpt || 'Affordable IPTV plans with no contracts or hidden fees'}
              </p>
            </motion.div>

            {/* Quick Answer */}
            <QuickAnswerSection answer={quickAnswer} />

            {/* Pricing Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {pricingPillarAIO.pricingPlans.map((plan, index) => (
                  <motion.div
                    key={plan.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                    className={`glass rounded-xl p-6 ${plan.recommended ? 'border-2 border-primary' : ''} relative`}
                    style={{ boxShadow: 'var(--shadow-glass)' }}
                  >
                    {plan.recommended && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-primary text-white text-xs font-bold px-4 py-1 rounded-full">
                          RECOMMENDED
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold text-primary">{plan.price}</span>
                        <span className="text-white/60">{plan.period}</span>
                      </div>
                      <p className="text-xs text-primary mt-2">{plan.annualPrice}/year • {plan.annualSavings}</p>
                    </div>

                    <div className="space-y-3 mb-6">
                      {plan.features.map(feature => (
                        <div key={feature} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-white/80">{feature}</span>
                        </div>
                      ))}
                      {plan.limitations.map(limitation => (
                        <div key={limitation} className="flex items-start gap-2 text-sm">
                          <XCircle className="w-4 h-4 text-white/30 flex-shrink-0 mt-0.5" />
                          <span className="text-white/40">{limitation}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pt-4 border-t border-white/10">
                      <p className="text-xs text-white/60 mb-4">
                        <strong className="text-white/90">Best for:</strong> {plan.bestFor}
                      </p>
                      <Button
                        asChild
                        className={`w-full ${plan.recommended ? 'bg-primary hover:bg-primary/90 text-white' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                      >
                        <a href="https://xtreamcube.shop/freetrialiptv/" target="_blank" rel="noopener noreferrer">
                          Start Free Trial
                        </a>
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* FAQ Section */}
            <div className="mt-16">
              <GateFAQSection faqItems={faqItems} />
            </div>

            {/* Comparison CTA */}
            <div className="mt-16">
              <CTA1
                headline="Save 60-80% vs Cable TV"
                subtext="Cable TV costs $100-200/month. XtreamCube Premium offers 20,000+ channels for $19.99/month. Save $960-2,160 annually."
                buttonText="Start Free Trial"
                buttonUrl="https://xtreamcube.shop/freetrialiptv/"
              />
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};
